import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MoreVertical, Pencil, RefreshCw } from 'lucide-react';
import type { PromisedTimeSuggestion, TicketRecord, TicketRepair } from '@/lib/api/tickets.js';
import { getPromisedTime } from '@/lib/api/tickets.js';
import { formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError } from '@/lib/toast.js';
import { StatusBadge, formatTicketStatus, useTicketStatusCatalog } from '@/components/tickets/StatusBadge.js';
import { WarrantyBadge } from '@/components/tickets/WarrantyBadge.js';
import { PrintButton } from '@/components/printing/PrintButton.js';
import { usePrintNodeConfigured } from '@/hooks/usePrintNodeConfigured.js';
import { TicketEditSlideOver } from '@/components/ticket-detail/TicketEditSlideOver.js';
import { PromisedTimeSuggestionModal } from '@/components/tickets/PromisedTimeSuggestionModal.js';

const FALLBACK_STATUSES = [
  'quote', 'intake', 'pending_sig', 'in_queue', 'diagnosing', 'waiting_auth',
  'waiting_parts', 'in_repair', 'on_hold', 'awaiting_approval', 'ready', 'pending_pay', 'closed',
  'warranty', 'cancelled', 'voided',
];

function assigneeName(ticket: TicketRecord) {
  const n = [ticket.assigneeFirstName, ticket.assigneeLastName].filter(Boolean).join(' ');
  return n || 'Unassigned';
}

export function CompactTicketHeader({
  ticket,
  repairs,
  users,
  onStatusChange,
  onClearOverride,
  onPriorityChange,
  onAssign,
  onSendSms,
  onSendEmail,
  onViewPortal,
  onCreateWarranty,
  onSendCareGuide,
  onCancel,
  onVoid,
  onDelete,
  onTransfer,
  onClone,
  canVoid,
  canDelete,
  canTransfer,
  canClone,
  onTicketUpdated,
  onSwitchToBench,
}: {
  ticket: TicketRecord;
  repairs?: TicketRepair[];
  users: Array<{ id: string; firstName: string; lastName: string }>;
  onStatusChange: (status: string) => void;
  onClearOverride?: () => void;
  onPriorityChange: (priority: string) => void;
  onAssign: (userId: string) => void;
  onSendSms: () => void;
  onSendEmail: () => void;
  onViewPortal: () => void;
  onCreateWarranty: () => void;
  onSendCareGuide: () => void;
  onCancel: () => void;
  onVoid: () => void;
  onDelete?: () => void;
  onTransfer?: () => void;
  onClone?: () => void;
  canVoid: boolean;
  canDelete?: boolean;
  canTransfer?: boolean;
  canClone?: boolean;
  warrantyActive: boolean;
  onTicketUpdated: () => void;
  onSwitchToBench?: () => void;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [promisedSuggestion, setPromisedSuggestion] = useState<PromisedTimeSuggestion | null>(null);
  const [showPromisedModal, setShowPromisedModal] = useState(false);
  const { hasPrintNode } = usePrintNodeConfigured('label');
  const statusesQuery = useTicketStatusCatalog(true);
  const statusOptions =
    (statusesQuery.data ?? []).filter((s) => s.active || s.slug === ticket.status).length > 0
      ? (statusesQuery.data ?? []).filter((s) => s.active || s.slug === ticket.status)
      : FALLBACK_STATUSES.map((slug) => ({
          slug,
          name: formatTicketStatus(slug),
          id: slug,
        }));

  async function handleRecalculateDueDate() {
    try {
      setRecalculating(true);
      const serviceIds = (repairs ?? [])
        .map((r) => r.serviceId)
        .filter(Boolean) as string[];
      const response = await getPromisedTime({
        serviceIds,
        assignedToId: ticket.assignedTo ?? undefined,
      });
      setPromisedSuggestion(response.data);
      setShowPromisedModal(true);
    } catch (err) {
      toastError(parseApiError(err).message || 'Failed to calculate promised time');
    } finally {
      setRecalculating(false);
    }
  }
  const stage = statusesQuery.data?.find((s) => s.slug === ticket.status)?.stage;
  const overdue = Boolean(
    ticket.promisedDate &&
      new Date(ticket.promisedDate) < new Date() &&
      !(stage
        ? ['closed', 'cancelled', 'ready'].includes(String(stage))
        : ['closed', 'cancelled', 'voided', 'ready'].includes(ticket.status)),
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-black text-slate-950">{ticket.ticketNumber}</h1>
        <WarrantyBadge isWarranty={ticket.isWarranty} />
        {ticket.importedFrom === 'repairdesk' ? (
          <span
            className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
            title="Imported from RepairDesk"
          >
            RepairDesk{ticket.externalId ? ` · #${ticket.externalId}` : ''}
          </span>
        ) : null}
        <StatusBadge status={ticket.status} catalog={statusesQuery.data ?? []} />
        {ticket.statusOverride ? (
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">
            Overridden
          </span>
        ) : (
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Derived</span>
        )}
        <select
          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold"
          value={ticket.status}
          onChange={(e) => onStatusChange(e.target.value)}
        >
          {statusOptions.map((s) => (
            <option key={'slug' in s ? s.slug : s} value={'slug' in s ? s.slug : s}>
              {'name' in s ? s.name : formatTicketStatus(String(s))}
            </option>
          ))}
        </select>
        {ticket.statusOverride && onClearOverride ? (
          <button
            type="button"
            className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900"
            onClick={onClearOverride}
          >
            Clear override
          </button>
        ) : null}
        <select
          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold capitalize"
          value={ticket.priority}
          onChange={(e) => onPriorityChange(e.target.value)}
        >
          {['normal', 'rush', 'urgent'].map((p) => (
            <option key={p} value={p}>{p === 'rush' ? '⚡ Rush' : prettyLabel(p)}</option>
          ))}
        </select>
        {onSwitchToBench ? (
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700"
            onClick={onSwitchToBench}
          >
            Bench View
          </button>
        ) : null}
        <button
          type="button"
          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
          title="Edit ticket"
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>📅 Created {formatDateTime(ticket.createdAt)}</span>
        <span className={overdue ? 'font-bold text-red-600' : ''}>
          ⏰ Due {ticket.promisedDate ? formatDateTime(ticket.promisedDate) : 'Not set'}
          {overdue ? ' (overdue)' : ''}
        </span>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          disabled={recalculating}
          onClick={() => void handleRecalculateDueDate()}
          title="Recalculate promised time"
        >
          <RefreshCw className={`h-3 w-3 ${recalculating ? 'animate-spin' : ''}`} />
          Recalculate
        </button>
        <span className="relative">
          👤 Assigned:{' '}
          <button
            type="button"
            className="font-semibold text-primary hover:underline"
            onClick={() => setAssignOpen((o) => !o)}
          >
            {assigneeName(ticket)}
          </button>
          {assignOpen ? (
            <div className="absolute left-0 top-full z-20 mt-1 max-h-48 w-48 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
              {users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                  onClick={() => {
                    onAssign(u.id);
                    setAssignOpen(false);
                  }}
                >
                  {u.firstName} {u.lastName}
                </button>
              ))}
            </div>
          ) : null}
        </span>
        {ticket.tags?.length ? (
          <span className="flex flex-wrap items-center gap-1">
            🏷
            {ticket.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                {tag}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <Link to="/tickets" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-950">
          <ArrowLeft className="h-4 w-4" />
          Back to Queue
        </Link>
        <div className="flex items-center gap-2">
          <PrintButton type="label" ticketId={ticket.id} hasPrintNode={hasPrintNode} />
          <div className="relative">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setMoreOpen((o) => !o)}
            >
              <MoreVertical className="h-4 w-4" />
              More
            </button>
            {moreOpen ? (
              <div className="absolute right-0 z-40 mt-1 w-56 rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-lg">
                <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onSendSms(); }}>Send SMS to Customer</button>
                <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onSendEmail(); }}>Send Email to Customer</button>
                <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onSendCareGuide(); }}>Send Care Guide</button>
                <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onViewPortal(); }}>View Customer Portal ↗</button>
                {canTransfer && onTransfer ? (
                  <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onTransfer(); }}>Transfer to Another Store</button>
                ) : null}
                {canClone && onClone ? (
                  <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onClone(); }}>Clone Ticket</button>
                ) : null}
                <button type="button" className="block w-full px-4 py-2 text-left hover:bg-slate-50" onClick={() => { setMoreOpen(false); onCreateWarranty(); }}>Create Warranty Ticket</button>
                <button type="button" className="block w-full px-4 py-2 text-left text-red-700 hover:bg-red-50" onClick={() => { setMoreOpen(false); onCancel(); }}>Cancel Ticket</button>
                {canVoid ? (
                  <button type="button" className="block w-full px-4 py-2 text-left text-red-800 hover:bg-red-50" onClick={() => { setMoreOpen(false); onVoid(); }}>Void Ticket</button>
                ) : null}
                {canDelete && onDelete ? (
                  <>
                    <div className="my-1 border-t border-slate-100" />
                    <button type="button" className="block w-full px-4 py-2 text-left text-red-800 hover:bg-red-50" onClick={() => { setMoreOpen(false); onDelete(); }}>Delete Ticket</button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <TicketEditSlideOver
        ticket={ticket}
        users={users}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={onTicketUpdated}
      />

      <PromisedTimeSuggestionModal
        open={showPromisedModal}
        ticketId={ticket.id}
        suggestion={promisedSuggestion}
        onClose={() => setShowPromisedModal(false)}
        onApplied={onTicketUpdated}
      />
    </section>
  );
}
